import { gql } from "lib/apolloClient";
import {
  templateItemDetailFragment,
  organizationPresetFragment,
  organizationFragment,
  teacherFragments,
  standardFragments,
  academicYearFragment,
  progressReportBaseTemplateFragment,
} from "./ToolingFragments";
import { designationFragment } from "modules/CommonFragments";

// Generic API for querying data required for internal tools
export const getInternalToolsGenericQuery = gql`
  query getInternalToolsGenericQuery(
    $apiType: INTERNAL_TOOL_API_TYPE_ENUM!
    $params: JSON!
  ) {
    platform {
      internalToolsGenericQuery(input: { apiType: $apiType, params: $params }) {
        isSuccess
        warning
        data
      }
    }
  }
`;

// query for getting configurations list
export const getGenericConfigurationsFeedQuery = gql`
  query genericConfigurationsFeed($filters: GenericConfigurationFeedFilters) {
    platform {
      genericConfigurationFeed(filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          startCursor
          endCursor
        }
        edges {
          node {
            id
            configuration
            aclModules
            aclPortalTypes
            aclControlParameterType
            configurationGroup
            dependentEntities {
              id
              entityGroup
            }
            description
            valueType
            value
            isLocked
            ownerTeam
            isDeprecated
            isDeleted
          }
        }
      }
    }
  }
`;

export const getMultiAccessIdentityFeedQuery = gql`
  query multiAccessIdentityFeed($filters: MultiAccessIdentityFeedFilters) {
    platform {
      multiAccessIdentityFeed(filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        edges {
          node {
            id
            identityId
            email
            firstName
            lastName
            configuration {
              isAccessManagerEnabled
              isOrganizationsEnabled
              isManageUsersEnabled
              isInternalToolsEnabled
              isActivateSchoolsEnabled
              isTemplateEditorEnabled
            }
            tags {
              id
              label
              type
              metadata
            }
            linkedIdentityUsersV2 {
              totalCount
            }
          }
          cursor
        }
      }
    }
  }
`;

export const getMultiAccessIdentitiesWithAMAccessQuery = gql`
  query multiAccessIdentitiesWithAMAccess {
    platform {
      multiAccessIdentitiesWithAMAccess {
        id
        identityId
        firstName
        lastName
        email
      }
    }
  }
`;

export const getManageLinkedUsersFeedQuery = gql`
  query getUserDetailsWithLinkedIdentities(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $first: Int
    $after: String
    $searchText: String
  ) {
    node(id: $id, type: $type) {
      id
      ... on MultiAccessIdentity {
        id
        identityId
        email
        firstName
        lastName
        linkedIdentityUsersV2(
          first: $first
          after: $after
          searchText: $searchText
        ) {
          totalCount
          edgesV2 {
            node {
              id
              identityId
              email
              firstName
              lastName
              type
              organization {
                id
                name
                logo
              }
              designation {
                id
                title
              }
              expiresAt
              isArchived
            }
          }
        }
      }
    }
  }
`;

// query for add mutation options
export const getConfigurationOptionsQuery = gql`
  query getAddConfigurationOptions {
    platform {
      configurationToolMutationOptions {
        configurationGroupOptions
        presetDependentEntities {
          id
          entityGroup
        }
        ownerTeams
        validitySupportingEntities
        supportedAppTypes
        supportedACLControlParameterTypes
        supportedACLPortalTypes
        supportedACLModules
      }
    }
  }
`;

export const getConfigurationValuesQuery = gql`
  query getConfigurationValues($filters: configurationValueFilters!) {
    platform {
      getConfigurationValues(filters: $filters) {
        values {
          value
          id
          entityGroup {
            entities {
              entityId
              entityType
            }
          }
          regionId
        }
      }
    }
  }
`;

// query to fetch single configuration item
export const getConfigurationDetailsQuery = gql`
  query getConfigurationDetails($id: ID!) {
    node(id: $id, type: CONFIGURATION) {
      ... on ConfigurationV2 {
        id
        configuration
        aclModules
        aclPortalTypes
        aclControlParameterType
        configurationGroup
        dependentEntities {
          id
          entityGroup
        }
        configurationView {
          id
          categoryKey
          viewGroupKey
          subGroupKey
          labelKey
          metadata
          uiType
          displaySequence
          platform
        }
        description
        valueType
        ownerTeam
        defaultValue: value
        validEntityGroups {
          entityGroupId
          entities {
            entityId
            entityType
          }
        }
        lockedEntityGroups {
          entityGroupId
          entities {
            entityId
            entityType
          }
        }
        parentConfigurations {
          id
          configuration
        }
        childConfigurations {
          id
          configuration
        }
      }
    }
  }
`;

export const getProgressReportBaseTemplateListQuery = gql`
  query getProgressReportBaseTemplateList {
    documentation {
      progressReportBaseTemplateList {
        ...baseTemplateList
      }
    }
  }
  ${progressReportBaseTemplateFragment.baseTemplateList}
`;

export const getProgressReportBaseTemplateQuery = gql`
  query getProgressReportBaseTemplate($id: ID!) {
    node(id: $id, type: PROGRESS_REPORT_BASE_TEMPLATE) {
      id
      ...baseTemplateItemDetails
    }
  }
  ${progressReportBaseTemplateFragment.baseTemplateItemDetails}
`;

export const getOrganizationPlannerTemplateQuery = gql`
  query getOrganizationPlannerTemplateDetails {
    platform {
      organization {
        id
        plannerTemplate {
          ...templateItem
        }
      }
    }
  }
  ${templateItemDetailFragment.templateItemDetail}
`;

// Transfer units tool

// Transfer units tool

export const getAllOrganizationQuery = gql`
  query getAllOrganizationDetails($searchText: String!, $after: String) {
    organizationManagement {
      organizationPresetFeed(
        first: 20
        searchText: $searchText
        after: $after
      ) {
        totalCount
        edges {
          node {
            ...organizationItem
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${organizationPresetFragment.orgItem}
`;

export const getOrganizationAttachmentDetailsQuery = gql`
  query getOrganizationAttachmentDetails($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        ...organizationAttachmentDetailsItem
      }
    }
  }
  ${organizationFragment.organizationAttachmentDetails}
`;

export const getOrganizationDetailsQuery = gql`
  query getOrganizationDetails(
    $id: ID!
    $globalConstantsFilter: GlobalConstantsFilterOptions
    $curriculumProgramFilters: CurriculumProgramFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        ...organizationDetailsItem
      }
    }
  }
  ${organizationFragment.organizationDetails}
`;
export const getOrganizationFeedQuery = gql`
  query getOrganizationFeed(
    $filters: OrganizationFeedConnectionFilter
    $after: String
    $tagFilters: TagFilters
    $globalConstantsFilter: GlobalConstantsFilterOptions
    $fetchGlobalConstants: Boolean = false
  ) {
    platform {
      toddleBuddies
      buddies {
        id
        firstName
        lastName
      }
      organizationFeed(first: 20, filters: $filters, after: $after) {
        totalCount
        feedAnalytics
        onboardingAnalytics
        transitionAnalytics
        subscriptionPlanAnalytics
        edges {
          node {
            ...organisationFeedDetailsItem
            tags(filters: $tagFilters) {
              id
              value
              key
            }
            globalConstants(filter: $globalConstantsFilter)
              @include(if: $fetchGlobalConstants) {
              id
              name
              value
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${organizationFragment.organisationFeedDetails}
`;

export const getOrganizationApprovalRequestFeed = gql`
  query getOrganizationApprovalRequestFeed($searchText: String) {
    organizationManagement {
      organizationApprovalRequestFeed(filters: { searchText: $searchText }) {
        id
        name
        type
        ibCode
        city
        state
        country
        firstName
        lastName
        email
        role
        url
        webUrl
        createdAt
        designation {
          ...designationItem
        }
      }
    }
  }
  ${designationFragment.designation}
`;

export const getUserApprovalRequestFeed = gql`
  query getUserApprovalRequestFeed($searchText: String) {
    organizationManagement {
      userApprovalRequestFeed(filters: { searchText: $searchText }) {
        id
        firstName
        lastName
        email
        role
        url
        webUrl
        createdAt
        designation {
          ...designationItem
        }
        organizationPreset {
          ...organizationItem
        }
      }
    }
  }
  ${organizationPresetFragment.orgItem}
  ${designationFragment.designation}
`;

// User Feed

export const getUserFeedQuery = gql`
  query getUserFeed(
    $searchText: String!
    $organizations: [String]
    $after: String
    $regionIds: [ID!]
  ) {
    platform {
      userFeed(
        first: 20
        filters: {
          searchText: $searchText
          organizations: $organizations
          regionIds: $regionIds
        }
        after: $after
      ) {
        totalCount
        edges {
          node {
            ...teacherItem
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${teacherFragments.teacherFeed}
`;

// Standards Feed Query

export const getStandardFeedQuery = gql`
  query getStandardFeed(
    $filters: standardBenchmarkSetFeedConnectionFilter
    $after: String
  ) {
    planner {
      standardBenchmarkSetFeed(first: 20, filters: $filters, after: $after) {
        totalCount
        edges {
          node {
            ...standardItem
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${standardFragments.standardFeed}
`;
export const getStandardBenchmarkSetDownloadUrlQuery = gql`
  query getStandardBenchmarkSetDownloadUrl($standardSetId: ID!) {
    planner {
      standardBenchmarkSetDownload(input: { standardSetId: $standardSetId })
    }
  }
`;

export const getAllGlobalSubjectsQuery = gql`
  query getAllGlobalSubjectsQuery {
    platform {
      allGlobalSubjects {
        ...globalSubjects
      }
    }
  }
  ${standardFragments.globalSubjects}
`;

// Academic Year List

export const getAcademicYears = gql`
  query getAcademicYears($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        academicYears {
          ...academicYear
        }
      }
    }
  }
  ${academicYearFragment.academicYear}
`;

// Configurable Settings List

export const fetchAllClassesOfOrgQuery = gql`
  query allClassesOfOrganisation($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        courses {
          edges {
            node {
              id
              title
            }
          }
        }
      }
    }
  }
`;

export const bulkDownloadOrganizationDataQuery = gql`
  query bulkDownlaodOrgData($filters: BulkDownloadOrganizationDataFilters!) {
    platform {
      bulkDownloadOrganizationData(filters: $filters)
    }
  }
`;

export const getIntegrationTaskCountQuery = gql`
  query getIntegrationTaskCount(
    $id: ID!
    $filters: IntegrationTaskCountFilter!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        integrationTaskCount(filters: $filters)
      }
    }
  }
`;

export const generateConfigurationDMLQuery = gql`
  query generateConfigurationDML($configurationId: ID!) {
    platform {
      generateConfigurationPortDML(configurationId: $configurationId)
    }
  }
`;

export const getManagerListQuery = gql`
  query getManagerListQuery($filters: MultiAccessIdentityFeedFilters) {
    platform {
      multiAccessIdentityFeed(filters: $filters) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            identityId
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export const getOrganizationAndUserRequestCountQuery = gql`
  query getOrganizationAndUserRequestCount(
    $filters: MultiAccessIdentityRequestFeedFilters
  ) {
    platform {
      multiAccessIdentityRequestFeed(filters: $filters) {
        totalCount
        __typename
      }
      multiAccessIdentityUserAccessRequestFeed(filters: $filters) {
        totalCount
        __typename
      }
      __typename
    }
  }
`;

export const getOrganizationPermissionRequestQuery = gql`
  query getOrganizationPermissionRequest(
    $filters: MultiAccessIdentityRequestFeedFilters
  ) {
    platform {
      multiAccessIdentityRequestFeed(filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
          __typename
        }
        edges {
          node {
            id
            requester {
              id
              firstName
              lastName
              identityId
              email
            }
            requestedManager {
              id
              identityId
              firstName
              lastName
              email
            }
            organization {
              id
              name
            }
            reason
            status
            expiresAt
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
`;

export const getOrganizationUserAccessRequestQuery = gql`
  query getOrganizationUserAccessRequest(
    $filters: MultiAccessIdentityRequestFeedFilters
  ) {
    platform {
      multiAccessIdentityUserAccessRequestFeed(filters: $filters) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
          __typename
        }
        edges {
          node {
            id
            requester {
              id
              firstName
              lastName
              identityId
              email
              __typename
            }
            requestedManager {
              id
              identityId
              firstName
              lastName
              email
              __typename
            }
            user {
              ... on Staff {
                id
                firstName
                lastName
                role
                organization {
                  id
                  name
                }
              }
              ... on Student {
                id
                firstName
                lastName
                organization {
                  id
                  name
                }
              }
            }
            reason
            status
            expiresAt
            __typename
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
`;

export const getSurveyFeedQuery = gql`
  query getAllSurveyFeeds {
    platform {
      getAllSurveys {
        isSuccess
        warning
        surveys {
          surveyId
          surveyTitle
          startDate
          endDate
          pageUrl
          description
          stages {
            stageId
            surveyId
            question
            questionType
            questionSubtext
            questionOptions
            stageOrder
            dependsOnStageId
            dependencyAnswer
          }
        }
      }
    }
  }
`;

export const getRuleSetFeedQuery = gql`
  query getAllSurveyRulesets {
    platform {
      getAllSurveyRulesets {
        isSuccess
        warning
        rulesets {
          id
          name
          rules {
            ruleId
            ruleType
            ruleValue
            operator
            ruleOrder
            afterGroupCondition
          }
        }
      }
    }
  }
`;

export const getRuleSetBasicDetailsQuery = gql`
  query getRulesetBasicDetails($filters: GetAllSurveyRulesetsFilter) {
    platform {
      getAllSurveyRulesets(filters: $filters) {
        isSuccess
        warning
        rulesets {
          id
          name
        }
      }
    }
  }
`;

export const getUnassignedTeamIdentitiesCountQuery = gql`
  query getUnassignedTeamIdentities($filters: MultiAccessIdentityFeedFilters) {
    platform {
      multiAccessIdentityFeed(filters: $filters) {
        totalCount
      }
    }
  }
`;

export const getCurriculumProgramByPlatformIdQuery = gql`
  query getCurriculumProgramByPlatformId($id: String!) {
    planner {
      curriculumProgramDataByPlatformId(id: $id) {
        error
        subscriptionPlan
        subscriptionStatus
        crmId
      }
    }
  }
`;

export const getOrgCopyPipelineStatusQuery = gql`
  query getOrgCopyPipelineStatus {
    platform {
      orgCopyPipelineStatus {
        buildNumber
        status
        sourceOrgId
        destinationRegion
        destinationEnvironment
      }
    }
  }
`;
